import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    LabelList,
    Cell,
} from "recharts";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

const COMPANY_GRADIENTS = [
    { id: "companyGrad1", start: "#ff6b35", end: "#ff9f7d" },
    { id: "companyGrad2", start: "#3b82f6", end: "#93c5fd" },
    { id: "companyGrad3", start: "#10b981", end: "#6ee7b7" },
    { id: "companyGrad4", start: "#f59e0b", end: "#fcd34d" },
    { id: "companyGrad5", start: "#8b5cf6", end: "#c4b5fd" },
    { id: "companyGrad6", start: "#ec4899", end: "#f9a8d4" },
    { id: "companyGrad7", start: "#06b6d4", end: "#67e8f9" },
    { id: "companyGrad8", start: "#ef4444", end: "#fca5a5" },
    { id: "companyGrad9", start: "#14b8a6", end: "#5eead4" },
    { id: "companyGrad10", start: "#f97316", end: "#fdba74" },
    { id: "companyGrad11", start: "#84cc16", end: "#bef264" },
    { id: "companyGrad12", start: "#eab308", end: "#fde047" },
    { id: "companyGrad13", start: "#d946ef", end: "#f0abfc" },
    { id: "companyGrad14", start: "#0ea5e9", end: "#7dd3fc" },
    { id: "companyGrad15", start: "#64748b", end: "#cbd5e1" },
];

const TopCompaniesActions = React.memo(({ orientation, onToggleOrientation, onMaximize, fullScreen }) => (
    <div style={{ display: "flex", gap: "20px", alignItems: "center", marginRight: fullScreen ? "50px" : "0" }}>
        <button
            onClick={onToggleOrientation}
            style={{
                padding: "6px 12px",
                fontSize: 12,
                background: orientation === "vertical" ? "rgba(255, 107, 53, 0.1)" : "rgba(59, 130, 246, 0.1)",
                color: orientation === "vertical" ? "#ff6b35" : "#60a5fa",
                border: `1px solid ${orientation === "vertical" ? "rgba(255, 107, 53, 0.3)" : "rgba(59, 130, 246, 0.3)"}`,
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
            }}
            title={orientation === "vertical" ? "Switch to Horizontal Layout" : "Switch to Vertical Layout"}
        >
            <span style={{ fontSize: "14px" }}>{orientation === "vertical" ? "↔" : "↕"}</span>
            {orientation === "vertical" ? "Horizontal View" : "Vertical View"}
        </button>
        {onMaximize && !fullScreen && (
            <button
                onClick={onMaximize}
                style={{
                    padding: "8px",
                    background: "rgba(255, 255, 255, 0.05)",
                    color: "#e5e7eb",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: 8,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                }}
                title="Maximize"
            >
                <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
            </button>
        )}
        {fullScreen && onMaximize && (
            <button
                onClick={onMaximize}
                style={{
                    padding: "8px",
                    background: "rgba(244, 67, 54, 0.2)",
                    color: "#f44336",
                    border: "1px solid #f44336",
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                }}
            >

            </button>
        )}
    </div>
));

const TopCompaniesSubtitle = React.memo(({ salesperson, year, region, fullScreen }) => (
    <p style={{
        color: "#f8fafc",
        fontSize: fullScreen ? "24px" : "18px",
        marginTop: fullScreen ? "0px" : "-10px",
        marginBottom: theme.spacing.lg,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        fontWeight: 700,
        letterSpacing: "0.5px",
        textAlign: fullScreen ? "center" : "left",
        textShadow: "0 2px 4px rgba(0,0,0,0.5)"
    }}>
        {salesperson && <span style={{ color: "#ff8a3d" }}>{salesperson}</span>}
        {year && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {year}</span>}
        {region && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {region === "all" ? "All Regions" : region}</span>}
    </p>
));

const TopCompaniesSummary = React.memo(({ totalValue, totalDeals, displayCurrency = "AED" }) => (
    <div style={{ display: "flex", gap: "20px", marginBottom: "8px" }}>
        <div style={{
            flex: 1,
            padding: "16px 20px",
            background: "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 107, 53, 0.02) 100%)",
            borderRadius: "16px",
            border: "1px solid rgba(255, 107, 53, 0.2)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            animation: "fadeIn 0.5s ease-out"
        }}>
            <span style={{ fontSize: "12px", color: "#ff6b35", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>Total Value</span>
            <span style={{ fontSize: "28px", fontWeight: "800", color: "#fff", marginTop: "4px" }}>
                {displayCurrency} <span className="text-gradient-orange">{Math.round(totalValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </span>
        </div>
        <div style={{
            flex: 1,
            padding: "16px 20px",
            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.02) 100%)",
            borderRadius: "16px",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start"
        }}>
            <span style={{ fontSize: "12px", color: "#60a5fa", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>Total Deals</span>
            <span style={{ fontSize: "28px", fontWeight: "800", color: "#fff", marginTop: "4px" }}>
                <span className="text-gradient-blue">{totalDeals.toLocaleString()}</span>
            </span>
        </div>
    </div>
));

function TopCompanies({ filterContext, bundleData, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const [data, setData] = useState({ byValue: [], byDeals: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [orientation, setOrientation] = useState("vertical");

    const processRows = useCallback((rows) => {
        if (!Array.isArray(rows)) throw new Error("Invalid data format");
        const rowsWithDealCount = rows.map(row => ({ ...row, deal_count: row.deal_count || 0 }));
        const sortedByValue = [...rowsWithDealCount].sort((a, b) => (b.value_aed || 0) - (a.value_aed || 0));
        const sortedByDeals = [...rowsWithDealCount].sort((a, b) => (b.deal_count || 0) - (a.deal_count || 0));
        return { byValue: sortedByValue, byDeals: sortedByDeals };
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(`${API_BASE}/api/metrics/top-companies`, {
                params: { salesperson, year, region: region || "all", limit: 15 },
            });
            const rows = res.data.data || [];
            setData(processRows(rows));
        } catch (err) {
            console.error("TopCompanies error", err);
            setError(err.message || "Failed to load top companies");
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region, processRows]);

    useEffect(() => {
        if (bundleData) {
            try {
                const rows = bundleData.data || [];
                setData(processRows(rows));
                setLoading(false);
                setError("");
            } catch (err) {
                console.error("Error processing bundle data:", err);
                setError("Failed to process top companies data");
            }
            return;
        }
        if (!salesperson || !year) return;
        fetchData();
    }, [salesperson, year, region, bundleData, processRows, fetchData]);

    const hasData = useMemo(() => data.byValue.length > 0, [data.byValue]);

    const totals = useMemo(() => ({
        totalValue: data.byValue.reduce((sum, d) => sum + (d.value_aed || 0), 0),
        totalDeals: data.byDeals.reduce((sum, d) => sum + (d.deal_count || 0), 0),
    }), [data.byValue, data.byDeals]);

    const handleToggleOrientation = useCallback(() => {
        setOrientation(prev => (prev === "vertical" ? "horizontal" : "vertical"));
    }, []);

    const actionButtons = useMemo(() => (
        <TopCompaniesActions
            orientation={orientation}
            onToggleOrientation={handleToggleOrientation}
            onMaximize={onMaximize}
            fullScreen={fullScreen}
        />
    ), [orientation, handleToggleOrientation, onMaximize, fullScreen]);

    const chartHeight = useMemo(() => {
        if (!fullScreen) return 350;
        if (orientation === "horizontal") {
            return Math.max(600, data.byValue.length * 60);
        }
        return 600;
    }, [fullScreen, orientation, data.byValue.length]);

    const ChartAreaValue = () => (
        <div style={{ flex: 1, minWidth: 0, marginRight: orientation === "horizontal" && !fullScreen ? "24px" : "0", display: "flex", flexDirection: "column" }}>
            <h3 style={{ color: "#e2e8f0", fontSize: "22px", marginBottom: "10px", textAlign: "center" }}>Total Value by Company</h3>
            <div style={{ minHeight: fullScreen ? 0 : 350 }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        data={data.byValue}
                        layout={orientation === "horizontal" ? "vertical" : "horizontal"}
                        margin={orientation === "horizontal" ? { top: 5, right: 40, left: fullScreen ? 80 : 60, bottom: 5 } : { top: 20, right: 10, left: 10, bottom: fullScreen ? 140 : 100 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={orientation === "horizontal"} horizontal={orientation !== "horizontal"} />
                        {orientation === "horizontal" ? (
                            <>
                                <XAxis
                                    type="number"
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: 12, fontWeight: 600 }}
                                    tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: `VALUE (${displayCurrency})`, position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="company"
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: fullScreen ? 13 : 11, fontWeight: 600 }}
                                    width={fullScreen ? 340 : 200}
                                    interval={0}
                                    tickFormatter={(value) => value}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: 'COMPANY', position: 'insideTopLeft', offset: 0, dy: -15, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
                                />
                            </>
                        ) : (
                            <>
                                <XAxis
                                    dataKey="company"
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: 11, fontWeight: 600 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={fullScreen ? 200 : 100}
                                    interval={0}
                                    tickFormatter={(value) => value.length > 25 ? value.substring(0, 22) + "..." : value}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: 'COMPANY', position: 'insideBottom', offset: -10, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
                                />
                                <YAxis
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: 12, fontWeight: 600 }}
                                    tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: `VALUE (${displayCurrency})`, angle: -90, position: 'insideLeft', offset: 10, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1, style: { textAnchor: 'middle' } }}
                                />
                            </>
                        )}
                        <Tooltip
                            cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                            content={(props) => {
                                if (!props.active || !props.payload || !props.payload[0]) return null;
                                const entry = props.payload[0].payload;
                                const index = data.byValue.findIndex(d => d.company === entry.company);
                                const gradient = COMPANY_GRADIENTS[index % COMPANY_GRADIENTS.length];
                                return (
                                    <div style={{ background: 'rgba(15, 23, 42, 0.95)', backdropFilter: 'blur(8px)', border: `1px solid ${gradient.end}`, borderRadius: '12px', padding: '12px 16px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', zIndex: 100 }}>
                                        <p style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: '14px' }}>{entry.company}</p>
                                        <p style={{ margin: '4px 0 0', fontWeight: 600, color: gradient.start }}>{displayCurrency} {Math.round(Number(entry.value_aed)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                );
                            }}
                        />
                        <defs>
                            {COMPANY_GRADIENTS.map(grad => (
                                <linearGradient key={grad.id} id={`${grad.id}-value`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={grad.start} stopOpacity={1} />
                                    <stop offset="100%" stopColor={grad.end} stopOpacity={0.8} />
                                </linearGradient>
                            ))}
                        </defs>
                        <Bar dataKey="value_aed" radius={[4, 4, 4, 4]} animationDuration={1000}>
                            {data.byValue.map((entry, index) => (
                                <Cell key={`cell-value-${entry.company}`} fill={`url(#${COMPANY_GRADIENTS[index % COMPANY_GRADIENTS.length].id}-value)`} style={{ transition: "all 0.3s ease" }} />
                            ))}
                            <LabelList dataKey="value_aed" position={orientation === "horizontal" ? "right" : "top"} fill="#fff" formatter={(v) => `${(v / 1_000_000).toFixed(1)}M`} style={{ fontSize: '11px', fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    const ChartAreaDeals = () => (
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <h3 style={{ color: "#e2e8f0", fontSize: "22px", marginBottom: "10px", textAlign: "center" }}>Total Deals by Company</h3>
            <div style={{ minHeight: fullScreen ? 0 : 350 }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        data={data.byDeals}
                        layout={orientation === "horizontal" ? "vertical" : "horizontal"}
                        margin={orientation === "horizontal" ? { top: 5, right: 40, left: fullScreen ? 80 : 60, bottom: 5 } : { top: 20, right: 10, left: 10, bottom: fullScreen ? 140 : 100 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={orientation === "horizontal"} horizontal={orientation !== "horizontal"} />
                        {orientation === "horizontal" ? (
                            <>
                                <XAxis
                                    type="number"
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: 12, fontWeight: 600 }}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: 'NO. OF DEALS', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="company"
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: fullScreen ? 13 : 11, fontWeight: 600 }}
                                    width={fullScreen ? 340 : 200}
                                    interval={0}
                                    tickFormatter={(value) => value}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: 'COMPANY', position: 'insideTopLeft', offset: 0, dy: -15, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
                                />
                            </>
                        ) : (
                            <>
                                <XAxis
                                    dataKey="company"
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: 11, fontWeight: 600 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={fullScreen ? 200 : 100}
                                    interval={0}
                                    tickFormatter={(value) => value.length > 25 ? value.substring(0, 22) + "..." : value}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: 'COMPANY', position: 'insideBottom', offset: -10, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}
                                />
                                <YAxis
                                    stroke="#ffffff"
                                    tick={{ fill: "#ffffff", fontSize: 12, fontWeight: 600 }}
                                    axisLine={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1.5 }}
                                    label={{ value: 'NO. OF DEALS', angle: -90, position: 'insideLeft', offset: 10, fill: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1, style: { textAnchor: 'middle' } }}
                                />
                            </>
                        )}
                        <Tooltip
                            cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                            content={(props) => {
                                if (!props.active || !props.payload || !props.payload[0]) return null;
                                const entry = props.payload[0].payload;
                                const valueIndex = data.byValue.findIndex(d => d.company === entry.company);
                                const gradientIndex = valueIndex >= 0 ? valueIndex : data.byDeals.findIndex(d => d.company === entry.company);
                                const gradient = COMPANY_GRADIENTS[gradientIndex % COMPANY_GRADIENTS.length];
                                return (
                                    <div style={{ background: 'rgba(15, 23, 42, 0.95)', backdropFilter: 'blur(8px)', border: `1px solid ${gradient.end}`, borderRadius: '12px', padding: '12px 16px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', zIndex: 100 }}>
                                        <p style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: '14px' }}>{entry.company}</p>
                                        <p style={{ margin: '4px 0 0', fontWeight: 600, color: gradient.start }}>{entry.deal_count} Deals</p>
                                    </div>
                                );
                            }}
                        />
                        <defs>
                            {COMPANY_GRADIENTS.map(grad => (
                                <linearGradient key={grad.id} id={`${grad.id}-deals`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={grad.start} stopOpacity={0.7} />
                                    <stop offset="100%" stopColor={grad.end} stopOpacity={0.5} />
                                </linearGradient>
                            ))}
                        </defs>
                        <Bar dataKey="deal_count" radius={[4, 4, 4, 4]} animationDuration={1000}>
                            {data.byDeals.map((entry, index) => {
                                const valueIndex = data.byValue.findIndex(d => d.company === entry.company);
                                const gradientIndex = valueIndex >= 0 ? valueIndex : data.byDeals.findIndex(d => d.company === entry.company);
                                const gradient = COMPANY_GRADIENTS[gradientIndex % COMPANY_GRADIENTS.length];
                                return (
                                    <Cell key={`cell-deals-${entry.company}`} fill={`url(#${gradient.id}-deals)`} style={{ transition: "all 0.3s ease" }} />
                                );
                            })}
                            <LabelList dataKey="deal_count" position={orientation === "horizontal" ? "right" : "top"} fill="#fff" style={{ fontSize: '11px', fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    if (!salesperson || !year) return null;

    return (
        <DashboardCard
            title="Top Companies"
            actions={actionButtons}
            fullScreen={fullScreen}
            gradient="primary"
        >
            <TopCompaniesSubtitle salesperson={salesperson} year={year} region={region} fullScreen={fullScreen} />
            {loading && <SkeletonCard height="400px" />}
            {error && (
                <div style={{ padding: theme.spacing.lg, background: "rgba(239, 68, 68, 0.1)", borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.danger}`, color: "#fca5a5" }}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            {!loading && !error && !hasData && (
                <EmptyState icon="️" message="No data found for this period." size="small" />
            )}
            {!loading && !error && hasData && (
                <div style={{ height: fullScreen ? "100%" : "auto", display: "flex", flexDirection: "column", overflowY: fullScreen ? "auto" : "visible" }}>
            <TopCompaniesSummary totalValue={totals.totalValue} totalDeals={totals.totalDeals} displayCurrency={displayCurrency} />
                    <div style={{ display: "flex", flexDirection: orientation === "horizontal" && !fullScreen ? "column" : "row", gap: "24px", marginTop: "20px", paddingBottom: fullScreen ? "40px" : "0" }}>
                        <ChartAreaValue />
                        {/* Divider line for clarity in split view */}
                        {fullScreen && <div style={{ width: "1px", background: "rgba(255,255,255,0.1)", margin: "0 10px" }}></div>}
                        <ChartAreaDeals />
                    </div>
                </div>
            )}
        </DashboardCard>
    );
}

export default TopCompanies;
